class FreeGiftHandler extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.variantId = this.dataset.giftVariantId;
    this.discountCode = this.dataset.discountCode;
    this.qualifies = this.dataset.qualifies === 'true';
    this.giftInCart = this.dataset.giftInCart === 'true';

    this.onCartUpdate = this.handleCartUpdate.bind(this);
    document.addEventListener('cart:update', this.onCartUpdate);

    // Initial check on load
    this.evaluateCartState();
  }

  disconnectedCallback() {
    document.removeEventListener('cart:update', this.onCartUpdate);
  }

  handleCartUpdate(event) {
    // If the cart update was triggered by us, ignore it to prevent infinite loops
    if (event.detail && event.detail.sourceId === 'free-gift-handler') {
      return;
    }

    // Fetch the updated section HTML to get the new qualification state
    this.fetchUpdatedSection();
  }

  async fetchUpdatedSection() {
    try {
      const response = await fetch(`${window.location.pathname}?section_id=${this.sectionId}`);
      if (!response.ok) return;
      
      const htmlText = await response.text();
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlText;
      const updatedElement = tempDiv.querySelector('free-gift-handler');
      
      if (updatedElement) {
        const oldQualifies = this.qualifies;
        const oldGiftInCart = this.giftInCart;

        this.qualifies = updatedElement.dataset.qualifies === 'true';
        this.giftInCart = updatedElement.dataset.giftInCart === 'true';
        
        // Detect manual removal: cart updated, we still qualify, but gift is no longer in cart
        // (meaning the user removed it directly from the cart).
        if (oldQualifies && oldGiftInCart && this.qualifies && !this.giftInCart) {
          sessionStorage.setItem(`rejected_gift_${this.variantId}`, 'true');
        }

        this.evaluateCartState();
      }
    } catch (e) {
      console.error('Error fetching free gift section state', e);
    }
  }

  evaluateCartState() {
    if (!this.variantId) return;

    const isRejected = sessionStorage.getItem(`rejected_gift_${this.variantId}`) === 'true';

    // Prevent overlapping updates
    if (window.freeGiftUpdating) return;

    if (this.qualifies && !this.giftInCart && !isRejected) {
      // Qualifies, gift not in cart, and hasn't been manually rejected -> Add it
      this.updateCart({
        updates: { [this.variantId]: 1 }
      }, this.discountCode);
    } else if (!this.qualifies && this.giftInCart) {
      // No longer qualifies but gift is in cart -> Remove it
      this.updateCart({
        updates: { [this.variantId]: 0 }
      });
    }
  }

  async updateCart(body, discountCode = null) {
    window.freeGiftUpdating = true;
    try {
      // Find sections that need to be updated in the DOM
      const sections = [];
      const cartDrawer = document.querySelector('cart-drawer-component');
      if (cartDrawer) sections.push(cartDrawer.dataset.sectionId || 'cart-drawer');
      
      const cartItems = document.querySelector('cart-items-component');
      if (cartItems) sections.push(cartItems.dataset.sectionId || 'main-cart-items');
      
      const header = document.querySelector('header-drawer-component, [id^="shopify-section-header"]');
      if (header) {
        // Many themes use 'cart-icon-bubble' as a specific section ID for the header cart count
        sections.push('cart-icon-bubble');
      }

      body.sections = sections.join(',');
      body.sections_url = window.location.pathname;

      if (discountCode) {
        // Appending discount to the cart update payload
        body.discount = discountCode;
      }

      const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(body)
      });
      
      const parsedState = await response.json();

      // Temporarily remove auto-open from cart drawer to prevent it from jumping/reopening
      const cartDrawerComponent = document.querySelector('cart-drawer-component');
      const hasAutoOpen = cartDrawerComponent?.hasAttribute('auto-open');
      if (hasAutoOpen) cartDrawerComponent.removeAttribute('auto-open');

      // Trigger the theme's cart update event to morph the sections in the DOM
      const event = new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          sourceId: 'free-gift-handler',
          data: {
            sections: parsedState.sections || {}
          }
        }
      });
      document.dispatchEvent(event);

      if (hasAutoOpen) {
        setTimeout(() => cartDrawerComponent.setAttribute('auto-open', ''), 50);
      }

    } catch (e) {
      console.error('Free Gift Update Error', e);
    } finally {
      window.freeGiftUpdating = false;
    }
  }
}

if (!customElements.get('free-gift-handler')) {
  customElements.define('free-gift-handler', FreeGiftHandler);
}
